import React from 'react';
import * as BizCharts from 'bizcharts';

const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const AXIS_Y_LINE_COLOR = '#D9D9D9';
const DEFAULT_HEIGHT = 430;
const DEFAULT_APPEND_PADDING: [number, number, number, number] = [57, 36, 40, 16];

export type PerformanceScatterChartProps = {
	data: any[];
	xField: string;
	yField: string;
	xAxisTitle: string;
	yAxisTitle: string;
	formatAxisLabel: (val: any) => string;
	/** BizCharts `color` prop value for the point geom (string or [fields, fn]). */
	pointColor: any;
	/** BizCharts `shape` prop value for the point geom (string or [fields, fn]). */
	pointShape: any;
	/** BizCharts `size` prop value for the point geom (number or [fields, fn]). */
	pointSize: any;
	/** BizCharts `style` prop value for the point geom (object or [fields, fn]). */
	pointStyle: any;
	/** Tooltip definition `[fields, formatter]` for the point geom. */
	pointTooltipDefinition: [string, (...args: any[]) => { name: string; value: string }];
	onPointClick?: (ev: any) => void;
	/** Additional scale config (e.g., performerGroup values order, nice scaling on axes). */
	scale?: Record<string, any>;
	/** When set, a `custom` Legend is rendered with these items; otherwise the default Legend is shown. */
	customLegendItems?: any[];
	height?: number;
	appendPadding?: [number, number, number, number];
};

const TOOLTIP_DOM_STYLES = {
	'g2-tooltip': {
		padding: '0',
		boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
		borderRadius: '4px',
	},
	'g2-tooltip-list': {
		margin: '0',
		padding: '0',
	},
} as const;

const ITEM_TPL = '<li data-index={index} style="list-style:none;padding:0;margin:0;">{value}</li>';

const buildAxisProps = (
	name: string,
	title: string,
	offset: number,
	formatAxisLabel: (val: any) => string
) => ({
	name,
	title: {
		text: title,
		style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
		offset,
	},
	label: {
		formatter: formatAxisLabel,
		style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
	},
	line: { style: { stroke: AXIS_Y_LINE_COLOR } },
	tickLine: null as any,
	grid: {
		line: {
			style: {
				stroke: AXIS_Y_LINE_COLOR,
				lineDash: [4, 4],
			},
		},
	},
});

const PerformanceScatterChart = ({
	data,
	xField,
	yField,
	xAxisTitle,
	yAxisTitle,
	formatAxisLabel,
	pointColor,
	pointShape,
	pointSize,
	pointStyle,
	pointTooltipDefinition,
	onPointClick,
	scale,
	customLegendItems,
	height = DEFAULT_HEIGHT,
	appendPadding = DEFAULT_APPEND_PADDING,
}: PerformanceScatterChartProps) => {
	const xAxis = buildAxisProps(xField, xAxisTitle, 40, formatAxisLabel);
	const yAxis = buildAxisProps(yField, yAxisTitle, 70, formatAxisLabel);

	return (
		<Chart
			height={height}
			appendPadding={appendPadding}
			data={data}
			autoFit={true}
			forceFit={true}
			theme="openwrench"
			scale={scale}
			onPointClick={onPointClick}
		>
			<Axis {...xAxis} />
			<Axis {...yAxis} />
			{customLegendItems ? (
				<Legend custom position="bottom" items={customLegendItems} />
			) : (
				<Legend position="bottom" />
			)}
			<Tooltip
				showTitle={false}
				crosshairs={false}
				itemTpl={ITEM_TPL}
				domStyles={TOOLTIP_DOM_STYLES}
			/>
			<Geom
				type="point"
				position={`${xField}*${yField}`}
				color={pointColor}
				shape={pointShape}
				size={pointSize}
				style={pointStyle}
				tooltip={pointTooltipDefinition}
			/>
		</Chart>
	);
};

export default PerformanceScatterChart;
